// ════════════════════════════════════════════════════════════════════
// Glass-i — Internal Breakage → Rework Request API client.
// Base: /api/erp/production/work-orders. Mirrors the workOrdersApi /
// proformaApprovalApi axios + unwrap() pattern.
//   report  → raise a breakage slip (one or many broken pieces)
//   list    → breakage requests filtered by status
//   counts  → per-status badge counts
//   get     → full request detail (header + item rows)
//   approve → L1 Approval-MFA gated; auto-issues remakes
//   reject  → decline the slip (reason required)
//   slipHtml→ printable-HTML fetched THROUGH the axios client so the
//             in-memory Bearer token is attached (a bare window.open to
//             the auth-gated slip route sends no header → AUTH_REQUIRED)
// ════════════════════════════════════════════════════════════════════

import client from '../../../../api/client';

const BASE = '/api/erp/production/work-orders';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined ? resp.data.data : (resp ? resp.data : null);
}
const enc = encodeURIComponent;

export const breakageApi = {
  // Raise a breakage slip. payload: { items:[{barcode,departmentCode,reasonCode,remarks?}], remarks? }
  report: (payload) => client.post(`${BASE}/breakage`, payload).then(unwrap),

  // status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' (omit for all).
  list: (status) => client.get(`${BASE}/breakage-requests`, {
    params: status ? { status } : undefined
  }).then(unwrap),

  counts: () => client.get(`${BASE}/breakage-requests/counts`).then(unwrap),

  get: (id) => client.get(`${BASE}/breakage-requests/${enc(id)}`).then(unwrap),

  // L1 Approval-MFA gated — forward the challenge id in the body so the
  // request still validates cleanly server-side (mirrors customerApi.updateStatus).
  approve: (id, challengeId) => client.post(
    `${BASE}/breakage-requests/${enc(id)}/approve`,
    challengeId ? { approvalMfaChallengeId: challengeId } : {}
  ).then(unwrap),

  reject: (id, reason) => client.post(
    `${BASE}/breakage-requests/${enc(id)}/reject`, { reason }
  ).then(unwrap),

  // Printable slip — a self-contained HTML document. Fetched via the axios
  // client (which injects the Authorization header) and returned as the raw
  // HTML string; transformResponse is bypassed so axios never tries to
  // JSON-parse the markup. The caller opens it in a new tab to print.
  slipHtml: (id) => client
    .get(`${BASE}/breakage-requests/${enc(id)}/slip`, {
      responseType: 'text',
      transformResponse: (data) => data
    })
    .then((resp) => resp.data),

  // Single-piece sticker (barcode + QR, 100×50 mm) as self-contained HTML —
  // reuses the shared work-order piece-sticker endpoint. Used to reprint the
  // REMAKE piece's sticker from an approved breakage slip.
  pieceSticker: (barcode) => client
    .get(`${BASE}/pieces/${enc(barcode)}/sticker`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
      transformResponse: (data) => data
    })
    .then((resp) => resp.data),

  // ALL remake stickers for an APPROVED slip in one 100×50 mm sheet — prints
  // every remade piece's label in a single action (vs one-at-a-time). Same
  // raw-HTML contract as pieceSticker; opened in the shared report viewer.
  remakeStickers: (id) => client
    .get(`${BASE}/breakage-requests/${enc(id)}/remake-stickers`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
      transformResponse: (data) => data
    })
    .then((resp) => resp.data)
};

export default breakageApi;
